import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { HtmlContent } from "@/components/RichText";
import { Plus } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableQuestion from "../SortableQuestion";
import { groupQuestionsBySection, reorderQuestionsWithinSection } from "../questionBuilder";

export default function QuestionsTab({ inst, set, addSection, addQuestion, updQuestion, rmQuestion, duplicateQuestion, changeQuestionType, addOption, updOption, rmOption, moveOption, addMatrixRow, updMatrixRow, rmMatrixRow, lang }) {
  // Expansion is UI-only and follows stable IDs through reorders and section moves.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const previousIds = useRef(new Set(inst.questions.map((question) => question.id)));
  useEffect(() => {
    const currentIds = new Set(inst.questions.map((question) => question.id));
    const addedIds = [...currentIds].filter((id) => !previousIds.current.has(id));
    previousIds.current = currentIds;
    setExpandedIds((current) => {
      const next = new Set([...current].filter((id) => currentIds.has(id)));
      addedIds.forEach((id) => next.add(id));
      return next.size === current.size && [...next].every((id) => current.has(id)) ? current : next;
    });
  }, [inst.questions]);
  const toggleExpanded = (id) => setExpandedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const groups = groupQuestionsBySection(inst.questions, inst.sections);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const reordered = reorderQuestionsWithinSection(inst.questions, active.id, over.id);
    if (reordered !== inst.questions) set("questions", reordered);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-muted/10 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold">{lang === "id" ? "Pertanyaan berdasarkan Bagian" : "Questions by Section"}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {lang === "id" ? "Seret pertanyaan di dalam Bagian yang sama, atau gunakan pemilih Bagian pada kartu." : "Drag within the same Section, or use the Section selector on each card."}
          </p>
        </div>
        <Button type="button" onClick={addSection} variant="outline" size="sm" className="rounded-full" data-testid="questions-add-section">
          <Plus className="mr-1 h-4 w-4" /> {lang === "id" ? "Tambah Bagian" : "Add Section"}
        </Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        {groups.map((group) => {
          const section = group.section;
          const title = section
            ? (section[`title_${lang}`] || section.title_id || section.title_en || (lang === "id" ? "Bagian tanpa judul" : "Untitled Section"))
            : (lang === "id" ? "Tanpa Bagian" : "Unassigned / No Section");
          const description = section ? (section[`description_${lang}`] || section.description_id || section.description_en || "") : "";
          return (
            <section key={group.id} className="overflow-hidden rounded-2xl border border-border bg-muted/5" data-testid={`question-group-${group.id}`}>
              <header className="border-b border-border bg-muted/25 px-4 py-4 sm:px-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {section ? `${lang === "id" ? "Bagian" : "Section"} ${section.order}` : (lang === "id" ? "Tidak dikelompokkan" : "Not grouped")}
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-foreground">{title}</h3>
                    {description && (
                      <HtmlContent html={description} className="mt-1.5 max-w-2xl text-xs leading-relaxed text-muted-foreground [&_p]:my-1 [&_ul]:pl-5 [&_ol]:pl-5" />
                    )}
                  </div>
                  <span className="rounded-full border border-border bg-background px-2.5 py-1 text-[11px] text-muted-foreground">
                    {group.questions.length} {lang === "id" ? "pertanyaan" : "questions"}
                  </span>
                </div>
              </header>

              <div className="space-y-4 p-3 sm:p-4">
                <SortableContext items={group.questions.map((question) => question.id)} strategy={verticalListSortingStrategy}>
                  {group.questions.map((question) => {
                    const questionIndex = inst.questions.findIndex((item) => item.id === question.id);
                    return (
                      <SortableQuestion
                        key={question.id}
                        id={question.id}
                        expanded={expandedIds.has(question.id)}
                        onToggleExpanded={() => toggleExpanded(question.id)}
                        q={question}
                        qi={questionIndex}
                        dims={inst.dimensions}
                        sections={inst.sections}
                        updQuestion={updQuestion}
                        rmQuestion={rmQuestion}
                        duplicateQuestion={duplicateQuestion}
                        addOption={addOption}
                        updOption={updOption}
                        rmOption={rmOption}
                        moveOption={moveOption}
                        addMatrixRow={addMatrixRow}
                        updMatrixRow={updMatrixRow}
                        rmMatrixRow={rmMatrixRow}
                        changeQuestionType={changeQuestionType}
                        scoring={inst.scoring}
                        lang={lang}
                      />
                    );
                  })}
                </SortableContext>
                {group.questions.length === 0 && (
                  <div className="rounded-xl border border-dashed border-border bg-background/60 px-4 py-5 text-sm text-muted-foreground">
                    {section
                      ? (lang === "id" ? "Bagian ini belum memiliki pertanyaan." : "This Section has no questions yet.")
                      : (lang === "id" ? "Tidak ada pertanyaan tanpa Bagian." : "There are no unassigned questions.")}
                  </div>
                )}
                <Button
                  type="button"
                  onClick={() => addQuestion(section?.id || null)}
                  variant="ghost"
                  size="sm"
                  className="text-terracotta-700"
                  data-testid={section ? `q-add-section-${section.id}` : "q-add"}
                >
                  <Plus className="mr-1 h-4 w-4" /> {lang === "id" ? "Tambah pertanyaan" : "Add question"}
                </Button>
              </div>
            </section>
          );
        })}
      </DndContext>
    </div>
  );
}
